import {
  Box,
  Card,
  EntityAvatar,
  EntityAvatarFallback,
  Icon,
  Text,
  getInitials,
  type BackgroundToken,
  type EntityAvatarSize,
  type IconName,
  Skeleton,
  TableCell,
  TableRow,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

// iOS-style segmented control: a muted track with the active segment raised on
// the lighter `background` surface. Clear in any theme (the ToggleGroup's
// selected wash is too subtle on dark surfaces). Active segments are
// chrome-less pressable tiles (`Box as="button"`), the sanctioned non-Button
// pressable pattern.
const ALIGN_SELF_CLASS = {
  start: "self-start",
  center: "self-center",
  end: "self-end",
} as const;

export function Segmented({
  label,
  value,
  onChange,
  options,
  alignSelf = "start",
}: Omittable<{
  /** The group's accessible name, for example "Scope". Each segment is a
   *  toggle button, so a screen reader reads "Scope, Workspace, pressed". */
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; icon?: IconName }[];
  /** Cross-axis placement of the track. `start` (default) hugs the leading
   *  edge in a column; `center` vertically centers it inside a flex row. */
  alignSelf?: "start" | "center" | "end";
}>) {
  return (
    <Box
      direction="row"
      gap="2xs"
      padding="2xs"
      rounded="md"
      surface="card"
      border
      align="center"
      role="group"
      aria-label={label}
      // Box carries no `alignSelf` prop.
      className={ALIGN_SELF_CLASS[alignSelf]}
    >
      {options.map((o, i) => {
        const active = o.value === value;
        return (
          <Box
            as="button"
            type="button"
            key={`${o.value}-${i}`}
            direction="row"
            align="center"
            gap="xs"
            paddingX="sm"
            paddingY="2xs"
            rounded="sm"
            background={active ? "background" : undefined}
            hoverBackground={!(active)}
            cursor="pointer"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
          >
            {o.icon ? (
              <Icon name={o.icon} size="sm" color={active ? undefined : "muted"} />
            ) : null}
            <Text
              size="sm"
              weight={active ? "medium" : "normal"}
              variant={active ? undefined : "muted"}
            >
              {o.label}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
}

// Summary stat tile: label over a big mono value with optional suffix/color.
export function StatCard({
  label,
  value,
  suffix,
  color,
}: Omittable<{
  label: string;
  value: string;
  suffix?: string;
  color?: "success" | "warning" | "destructive";
}>) {
  return (
    // Card has no `padding`/`fill`/`variant` props — Box supplies them.
    <Card render={<Box padding="md" grow />} radius="lg">
      <Text size="xs" variant="muted">
        {label}
      </Text>
      <Box direction="row" align="baseline" gap="xs" paddingTop="2xs">
        <Text size="2xl" weight="semibold" variant={color} mono>
          {value}
        </Text>
        {suffix ? (
          <Text size="sm" variant="muted">
            {suffix}
          </Text>
        ) : null}
      </Box>
    </Card>
  );
}

// A small colored status dot (service health, flag state).
export function StatusDot({
  background,
}: {
  background: BackgroundToken;
}) {
  return <Box width={8} height={8} rounded="full" background={background} />;
}

/**
 * The severity token every System nav dot reads, decided in one place so two
 * surfaces cannot render different verdicts about the same subject.
 *
 * `known` is the load: green is an affirmative "this is fine" claim, and every
 * count is zero both before the first response and after one that failed, so
 * "nothing is wrong" and "nothing is known" are the same numbers. Neutral until
 * something has actually been read.
 */
export function statusDotToken(
  known: boolean,
  bad: boolean,
  warn: boolean,
): BackgroundToken {
  if (!known) return "muted";
  if (bad) return "destructive";
  return warn ? "warning" : "success";
}

// Initials avatar for workspace marks — workspaces aren't an entity-chip kind,
// so the entity system can't resolve them. (User/superuser rows go through
// EntityTag.) Uses the entity-group particle's avatar shell, which owns the
// size/shape scale and the default fallback background.
export function InitialsAvatar({
  name,
  size = "default",
  shape = "circle",
}: Omittable<{
  name: string | null | undefined;
  size?: EntityAvatarSize;
  shape?: "circle" | "square";
}>) {
  const initials = getInitials(name, size);
  return (
    <EntityAvatar size={size} shape={shape} alt={name ?? "user"}>
      <EntityAvatarFallback icon={initials ? undefined : "user"}>
        {initials ? (
          <Text size="xs" weight="bold">
            {initials}
          </Text>
        ) : null}
      </EntityAvatarFallback>
    </EntityAvatar>
  );
}

// Bordered, rounded surface wrapping a table or panel. `Card` is already a
// bordered rounded surface with no padding of its own, and it keeps its
// `bg-card` fill (DESIGN-TUNABLE).
export function PanelCard({ children }: { children: React.ReactNode }) {
  return (
    <Card radius="lg" className="overflow-hidden">
      {children}
    </Card>
  );
}

// Per-screen header: title + monospace meta line + right-aligned actions.
export function ScreenHeader({
  title,
  meta,
  actions,
}: Omittable<{
  title: string;
  meta?: string;
  actions?: React.ReactNode;
}>) {
  return (
    <Box
      direction="row"
      align="center"
      gap="md"
      paddingX="lg"
      paddingTop="md"
      paddingBottom="sm"
    >
      <Text size="xl" weight="semibold">
        {title}
      </Text>
      {meta ? (
        <Text size="sm" variant="muted" mono>
          {meta}
        </Text>
      ) : null}
      <Box grow />
      {actions ? (
        <Box direction="row" align="center" gap="sm">
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}

/** A column of a loading admin table: the bar width follows the column's own
 *  sizing — `fill` cycles the prose widths, a number is that fixed column
 *  width — and `align` mirrors the header's trailing-edge numeric columns, in
 *  the same vocabulary `TableHead align` speaks. */
export type TableSkeletonColumn = Omittable<{
  width?: number;
  fill?: boolean;
  align?: "start" | "end";
}>;

// Body rows standing in for a table whose rows are still loading, under a
// header that already renders real. One bar per column, at the column's own
// width and alignment, so the settled rows land in place. The header is the
// stable frame; only the rows are unknown, so only the rows are bars.
export function TableRowsSkeleton({
  columns,
  rows = 4,
}: Omittable<{ columns: readonly TableSkeletonColumn[]; rows?: number }>) {
  return (
    <>
      {Array.from({ length: rows }, (_, row) => (
        <TableRow key={row}>
          {columns.map((column, index) => (
            <TableCell key={index}>
              {/* Skeleton is a block, so the cell's text alignment cannot
                  place it — the bar is aligned by its own flex parent. */}
              <Box align={column.align ?? "start"}>
                <Skeleton
                  delayed
                  height={12}
                  vary={column.fill ? row + index : undefined}
                  width={
                    column.fill
                      ? 160
                      : Math.max(24, Math.round((column.width ?? 48) * 0.6))
                  }
                />
              </Box>
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
